import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Avatar,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  SelectChangeEvent
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, updateUserProfile, UserProfile } from '../firebase/services';
import { useLanguage, TranslationValue } from '../context/LanguageContext';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

// Interface for tab panel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab Panel component
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Helper function to convert TranslationValue to string
const toStr = (value: TranslationValue): string => {
  return Array.isArray(value) ? value.join(' ') : value;
};

const SettingClient: React.FC = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Form fields
  const [formData, setFormData] = useState({
    // Personal information
    displayName: '',
    email: '',
    phoneNumber: '',
    avatarUrl: '',
    
    // Company information
    companyName: '',
    jobTitle: '',
    industry: '',
    companySize: '',
    
    // Address information
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    
    // Notification preferences
    emailNotifications: true,
    pushNotifications: true,
    marketingEmails: false
  });

  // Error states
  const [errors, setErrors] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (currentUser) {
        setLoading(true);
        try {
          const profile = await getUserProfile(currentUser.uid);
          if (profile) {
            setUserProfile(profile);
            setFormData({
              displayName: profile.displayName || '',
              email: profile.email || '',
              phoneNumber: profile.phoneNumber || '',
              avatarUrl: profile.avatarUrl || '',
              companyName: profile.companyName || '',
              jobTitle: profile.jobTitle || '',
              industry: profile.industry || '',
              companySize: profile.companySize || '',
              address: profile.address || '',
              city: profile.city || '',
              state: profile.state || '',
              zipCode: profile.zipCode || '',
              country: profile.country || '',
              emailNotifications: profile.emailNotifications !== false,
              pushNotifications: profile.pushNotifications !== false,
              marketingEmails: profile.marketingEmails === true
            });
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          showNotification(toStr(t('settingclient.errors.fetchProfile')), 'error');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserProfile();
  }, [currentUser, t]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value
    });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...errors };

    // Validate display name
    if (!formData.displayName.trim()) {
      newErrors.displayName = toStr(t('settingclient.errors.requiredField'));
      valid = false;
    } else {
      newErrors.displayName = '';
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = toStr(t('settingclient.errors.requiredField'));
      valid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = toStr(t('settingclient.errors.invalidEmail'));
      valid = false;
    } else {
      newErrors.email = '';
    }

    setErrors(newErrors);
    return valid;
  };

  const validatePasswordForm = () => {
    let valid = true;
    const newErrors = { ...errors };

    // Validate current password
    if (!currentPassword) {
      newErrors.currentPassword = toStr(t('settingclient.errors.requiredField'));
      valid = false;
    } else {
      newErrors.currentPassword = '';
    }

    // Validate new password
    if (!newPassword) {
      newErrors.newPassword = toStr(t('settingclient.errors.requiredField'));
      valid = false;
    } else if (newPassword.length < 8) {
      newErrors.newPassword = toStr(t('settingclient.errors.passwordLength'));
      valid = false;
    } else {
      newErrors.newPassword = '';
    }

    // Validate confirm password
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = toStr(t('settingclient.errors.passwordMismatch'));
      valid = false;
    } else {
      newErrors.confirmPassword = '';
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSaveProfile = async () => {
    if (!validateForm()) return;
    if (!currentUser) return;

    setSaving(true);
    try {
      // Update profile in Firestore
      await updateUserProfile(currentUser.uid, {
        displayName: formData.displayName,
        phoneNumber: formData.phoneNumber,
        avatarUrl: formData.avatarUrl,
        companyName: formData.companyName,
        jobTitle: formData.jobTitle,
        industry: formData.industry,
        companySize: formData.companySize,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
        emailNotifications: formData.emailNotifications,
        pushNotifications: formData.pushNotifications,
        marketingEmails: formData.marketingEmails
      });

      // Update display name in Firebase Auth
      await updateProfile(currentUser, {
        displayName: formData.displayName
      });

      showNotification(toStr(t('settingclient.notifications.profileUpdated')), 'success');
    } catch (error) {
      console.error("Error updating profile:", error);
      showNotification(toStr(t('settingclient.errors.updateProfile')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) return;
    if (!currentUser || !currentUser.email) return;

    setSaving(true);
    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, newPassword);

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      showNotification(toStr(t('settingclient.notifications.passwordUpdated')), 'success');
    } catch (error) {
      console.error("Error changing password:", error);
      showNotification(toStr(t('settingclient.errors.updatePassword')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 2,
            background: 'url(/resources/myhub.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.14)',
              zIndex: 0
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                color: '#ffffff',
                textShadow: '1px 1px 3px rgba(0, 0, 0, 0.7)'
              }}
            >
              {t('settingclient.title')}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 2, color: '#ffffff', textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)' }}>
              {t('settingclient.subtitle')}
            </Typography>
          </Box>
        </Paper>

        {/* Tabs Navigation */}
        <Paper elevation={0} sx={{ borderRadius: 2, mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="settings tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                fontWeight: 600,
                minHeight: 64
              }
            }}
          >
            <Tab icon={<PersonIcon />} iconPosition="start" label={t('settingclient.tabs.personal')} />
            <Tab icon={<BusinessIcon />} iconPosition="start" label={t('settingclient.tabs.company')} />
            <Tab icon={<LocationIcon />} iconPosition="start" label={t('settingclient.tabs.address')} />
            <Tab icon={<SecurityIcon />} iconPosition="start" label={t('settingclient.tabs.security')} />
            <Tab icon={<NotificationsIcon />} iconPosition="start" label={t('settingclient.tabs.notifications')} />
          </Tabs>

          {/* Personal Information Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {t('settingclient.personal.title')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar 
                  src={formData.avatarUrl} 
                  alt={formData.displayName}
                  sx={{ width: 120, height: 120, mb: 2 }}
                >
                  {formData.displayName?.charAt(0) || 'U'}
                </Avatar>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCameraIcon />}
                  sx={{ mb: 1 }}
                >
                  {t('settingclient.personal.uploadPhoto')}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" align="center">
                  {t('settingclient.personal.photoRequirements')}
                </Typography>
              </Box>
              
              <Box sx={{ flexGrow: 1 }}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label={t('settingclient.personal.displayName')}
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    error={!!errors.displayName}
                    helperText={errors.displayName}
                    required
                  />
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                    <TextField
                      fullWidth
                      label={t('settingclient.personal.email')}
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      required
                      disabled
                    />
                    <TextField
                      fullWidth
                      label={t('settingclient.personal.phoneNumber')}
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber}
                    />
                  </Box>
                </Stack>
              </Box>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? t('common.saving') : t('common.saveChanges')}
              </Button>
            </Box>
          </TabPanel>

          {/* Company Information Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {t('settingclient.company.title')}
            </Typography>
            
            <Stack spacing={3}>
              <TextField
                fullWidth
                label={t('settingclient.company.companyName')}
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
              />
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <TextField
                  fullWidth
                  label={t('settingclient.company.jobTitle')}
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                />
                <FormControl fullWidth>
                  <InputLabel>{t('settingclient.company.industry')}</InputLabel>
                  <Select
                    name="industry"
                    value={formData.industry}
                    label={t('settingclient.company.industry')}
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="technology">{t('settingclient.company.industries.technology')}</MenuItem>
                    <MenuItem value="finance">{t('settingclient.company.industries.finance')}</MenuItem>
                    <MenuItem value="healthcare">{t('settingclient.company.industries.healthcare')}</MenuItem>
                    <MenuItem value="education">{t('settingclient.company.industries.education')}</MenuItem>
                    <MenuItem value="retail">{t('settingclient.company.industries.retail')}</MenuItem>
                    <MenuItem value="manufacturing">{t('settingclient.company.industries.manufacturing')}</MenuItem>
                    <MenuItem value="other">{t('settingclient.company.industries.other')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <FormControl fullWidth>
                <InputLabel>{t('settingclient.company.companySize')}</InputLabel>
                <Select
                  name="companySize"
                  value={formData.companySize}
                  label={t('settingclient.company.companySize')}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="1-10">{t('settingclient.company.sizes.small')}</MenuItem>
                  <MenuItem value="11-50">{t('settingclient.company.sizes.medium')}</MenuItem>
                  <MenuItem value="51-200">{t('settingclient.company.sizes.large')}</MenuItem>
                  <MenuItem value="201-500">{t('settingclient.company.sizes.xlarge')}</MenuItem>
                  <MenuItem value="501+">{t('settingclient.company.sizes.enterprise')}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? t('common.saving') : t('common.saveChanges')}
              </Button>
            </Box>
          </TabPanel>

          {/* Address Information Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {t('settingclient.address.title')}
            </Typography>
            
            <Stack spacing={3}>
              <TextField
                fullWidth
                label={t('settingclient.address.streetAddress')}
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <TextField
                  fullWidth
                  label={t('settingclient.address.city')}
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                />
                <TextField
                  fullWidth
                  label={t('settingclient.address.state')}
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <TextField
                  fullWidth
                  label={t('settingclient.address.zipCode')}
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                />
                <FormControl fullWidth>
                  <InputLabel>{t('settingclient.address.country')}</InputLabel>
                  <Select
                    name="country"
                    value={formData.country}
                    label={t('settingclient.address.country')}
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="US">{t('settingclient.address.countries.us')}</MenuItem>
                    <MenuItem value="CA">{t('settingclient.address.countries.ca')}</MenuItem>
                    <MenuItem value="UK">{t('settingclient.address.countries.uk')}</MenuItem>
                    <MenuItem value="FR">{t('settingclient.address.countries.fr')}</MenuItem>
                    <MenuItem value="DE">{t('settingclient.address.countries.de')}</MenuItem>
                    <MenuItem value="other">{t('settingclient.address.countries.other')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? t('common.saving') : t('common.saveChanges')}
              </Button>
            </Box>
          </TabPanel>

          {/* Security Tab */}
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {t('settingclient.security.title')}
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('settingclient.security.passwordInfo')}
            </Alert>
            
            <Stack spacing={3}>
              <TextField
                fullWidth
                type={showCurrentPassword ? 'text' : 'password'}
                label={t('settingclient.security.currentPassword')}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label={t('settingclient.security.newPassword')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                error={!!errors.newPassword}
                helperText={errors.newPassword}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label={t('settingclient.security.confirmPassword')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                required
              />
            </Stack>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleChangePassword}
                disabled={saving}
              >
                {saving ? t('common.saving') : t('settingclient.security.changePassword')}
              </Button>
            </Box>
          </TabPanel>

          {/* Notifications Tab */}
          <TabPanel value={tabValue} index={4}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {t('settingclient.notifications.title')}
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {t('settingclient.notifications.preferences')}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.emailNotifications}
                    onChange={handleSwitchChange}
                    name="emailNotifications"
                    color="primary"
                  />
                }
                label={t('settingclient.notifications.emailNotifications')}
              />
              
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                {t('settingclient.notifications.emailDescription')}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.pushNotifications}
                    onChange={handleSwitchChange}
                    name="pushNotifications"
                    color="primary"
                  />
                }
                label={t('settingclient.notifications.pushNotifications')}
              />
              
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                {t('settingclient.notifications.pushDescription')}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {t('settingclient.notifications.marketingTitle')}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.marketingEmails}
                    onChange={handleSwitchChange}
                    name="marketingEmails"
                    color="primary"
                  />
                }
                label={t('settingclient.notifications.marketingEmails')}
              />
              
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                {t('settingclient.notifications.marketingDescription')}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? t('common.saving') : t('common.saveChanges')}
              </Button>
            </Box>
          </TabPanel>
        </Paper>
      </Container>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingClient; 